import { useState } from 'react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { Button, Modal, Input, Select, Autocomplete } from '@/components/ui';
import { useCargoListFormOptions } from '@/hooks/useCargoListFormOptions';
import { getWriteErrorMessage } from '@/utils/apiError';
import type { CargoItem, UpdateCargoRequest } from '@/types/cargoList.types';
import { useCargoListStore } from '@/store';

interface UpdateCargoModalProps {
  isOpen: boolean;
  onClose: () => void;
  cargoItem: CargoItem;
  onSuccess?: () => void;
}

const validationSchema = Yup.object({
  charterer: Yup.string().required('Charterer is required'),
  trade: Yup.string().required('Trade is required'),
  lcStart: Yup.date().required('Laycan start date is required'),
  lcEnd: Yup.date()
    .required('Laycan end date is required')
    .min(Yup.ref('lcStart'), 'End date must be after start date'),
  cargoSize: Yup.number()
    .typeError('Cargo size must be a number')
    .required('Cargo size is required')
    .min(40000, 'Cargo size must be at least 40,000 MT')
    .max(700000, 'Cargo size cannot exceed 700,000 MT'),
  status: Yup.number().required('Status is required'),
  certainty: Yup.number().required('Certainty is required'),
  privacyLevel: Yup.number().required('Privacy level is required'),
  laytime: Yup.number()
    .typeError('Laytime must be a number')
    .min(0, 'Laytime cannot be negative')
    .max(99999, 'Laytime cannot exceed 99999'),
  primaryComm: Yup.number()
    .typeError('Commission must be a number')
    .min(0, 'Commission cannot be negative')
    .max(9, 'Commission cannot exceed 9%'),
  addressComm: Yup.number()
    .typeError('Commission must be a number')
    .min(0, 'Commission cannot be negative')
    .max(9, 'Commission cannot exceed 9%'),
  primaryBroker: Yup.string().nullable(),
  rateAssessor: Yup.string().nullable(),
});

export function UpdateCargoModal({ isOpen, onClose, cargoItem, onSuccess }: UpdateCargoModalProps) {
  const [submitError, setSubmitError] = useState<string | null>(null);
  const { updateCargo } = useCargoListStore();

  const {
    chartererOptions,
    brokerOptions,
    rateAssessorOptions,
    tradeOptions,
    privacyLevelOptions,
    fixtureStatusOptions,
    certaintyCategoryOptions,
    loading,
    error,
  } = useCargoListFormOptions();

  // Convert cargo item data to form values
  const getInitialValues = () => ({
    charterer: cargoItem.charterer || '',
    trade: cargoItem.trade || '',
    primaryBroker: cargoItem.primaryBroker || '',
    rateAssessor: cargoItem.rateAssessor || '',
    lcStart: cargoItem.lcStart.split('T')[0], // Convert to YYYY-MM-DD format
    lcEnd: cargoItem.lcEnd.split('T')[0], // Convert to YYYY-MM-DD format
    status: cargoItem.statusId,
    certainty: cargoItem.certaintyId,
    privacyLevel: cargoItem.privacyLevelId,
    cargoSize: cargoItem.cargoSize.toString(),
    laytime: cargoItem.laytime,
    // toFixed(6) avoids the floating-point drift you'd get from a bare
    // `decimal * 100` (e.g. 0.013 * 100 = 1.3000000000000003), matching Angular.
    primaryComm: cargoItem.primaryComm ? +(cargoItem.primaryComm * 100).toFixed(6) : 0,
    addressComm: cargoItem.addressComm ? +(cargoItem.addressComm * 100).toFixed(6) : 0,
  });

  const handleSubmit = async (
    values: ReturnType<typeof getInitialValues>,
    { setSubmitting }: { setSubmitting: (isSubmitting: boolean) => void }
  ) => {
    setSubmitError(null);

    try {
      // Resolve the selected option. The Autocomplete sets the value to option.id
      // on selection, but the initial value is the name string from the cargo item.
      // We also need a fallback to the cargo's original id when the form value still
      // matches the cargo's original name — otherwise an unchanged value whose
      // option wasn't in the (capped) initial options list would be lost on save.
      const resolveId = (
        options: { id: number | string; name: string }[],
        value: unknown,
        originalName: string | null | undefined,
        originalId: number | null | undefined
      ): number | null => {
        const found = options.find((opt) => String(opt.id) === String(value) || opt.name === value);
        if (found) return Number(found.id);
        if (value && value === originalName && originalId != null) return originalId;
        return null;
      };

      const chartererId = resolveId(
        chartererOptions,
        values.charterer,
        cargoItem.charterer,
        cargoItem.chartererId
      );
      const tradeId = resolveId(tradeOptions, values.trade, cargoItem.trade, cargoItem.tradeId);
      if (chartererId == null) {
        throw new Error('Charterer not found. Please pick one from the suggestions.');
      }
      if (tradeId == null) {
        throw new Error('Trade not found. Please pick one from the suggestions.');
      }

      const rateAssessorId = values.rateAssessor
        ? resolveId(
            rateAssessorOptions,
            values.rateAssessor,
            cargoItem.rateAssessor,
            cargoItem.rateAssessorId
          )
        : null;
      if (values.rateAssessor && rateAssessorId == null) {
        throw new Error(
          'Rate assessor not found. Please pick one from the suggestions or clear the field.'
        );
      }

      const primaryBrokerId = values.primaryBroker
        ? resolveId(
            brokerOptions,
            values.primaryBroker,
            cargoItem.primaryBroker,
            cargoItem.primaryBrokerId
          )
        : null;
      if (values.primaryBroker && primaryBrokerId == null) {
        throw new Error(
          'Broker not found. Please pick one from the suggestions or clear the field.'
        );
      }

      // Create API request payload. The backend's CargoListRequest marks
      // ReportedTime/IsOnDailyReport/IsFixedDirect/BrokerDidNotShare as [Required],
      // so we pass through the existing cargo values (the modal doesn't edit them).
      const updateRequest: UpdateCargoRequest = {
        ReportedTime: new Date(cargoItem.reportedTime).toISOString(),
        TradeId: tradeId,
        StatusId: Number(values.status),
        CertaintyId: Number(values.certainty),
        PrivacyLevelId: Number(values.privacyLevel),
        IsOnDailyReport: cargoItem.isOnDailyReport,
        IsFixedDirect: cargoItem.isFixedDirect,
        BrokerDidNotShare: cargoItem.brokerDidNotShare,
        ChartererId: chartererId,
        CargoSize: parseFloat(values.cargoSize),
        RateAssessorId: rateAssessorId,
        PrimaryBrokerId: primaryBrokerId,
        PrimaryBrokerCommission: Number(values.primaryComm) / 100,
        AddressCommission: Number(values.addressComm) / 100,
        LayCanStart: new Date(values.lcStart).toISOString(),
        LayCanEnd: new Date(values.lcEnd).toISOString(),
        Laytime: Number(values.laytime),
      };

      await updateCargo(cargoItem.id, updateRequest);

      onSuccess?.();
      onClose();
    } catch (err) {
      setSubmitError(getWriteErrorMessage(err, 'Failed to update cargo'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Update Cargo" size="xl">
      <div className="mx-auto max-w-4xl">
        <Formik
          initialValues={getInitialValues()}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting }) => {
            return (
              <Form className="space-y-6">
                {submitError && (
                  <div className="rounded-md border border-red-200 bg-red-50 p-4">
                    <p className="text-sm text-red-600">{submitError}</p>
                  </div>
                )}

                {error && (
                  <div className="rounded-md border border-red-200 bg-red-50 p-4">
                    <p className="text-sm text-red-600">Error loading form data: {error}</p>
                  </div>
                )}

                {/* Cargo Information */}
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                    Cargo Information
                  </h3>
                  <div className="space-y-4">
                    <Autocomplete
                      name="charterer"
                      label="Charterer"
                      placeholder="Search for charterer..."
                      options={chartererOptions}
                      loading={loading}
                    />

                    <Autocomplete
                      name="trade"
                      label="Trade"
                      placeholder="Search for trade..."
                      options={tradeOptions}
                      loading={loading}
                    />

                    <Input
                      name="cargoSize"
                      label="Cargo Size (MT)"
                      type="number"
                      min={40000}
                      max={700000}
                      placeholder="40,000 - 700,000"
                    />
                  </div>
                </div>

                {/* Laycan Schedule */}
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                    Laycan Schedule
                  </h3>
                  <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
                    <Input name="lcStart" label="Start" type="date" />
                    <Input name="lcEnd" label="End" type="date" />
                    <Input name="laytime" label="Laytime (Hrs)" type="number" placeholder="24" />
                  </div>
                </div>

                {/* Status & Classification */}
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                    Status & Classification
                  </h3>
                  <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
                    <Select name="status" label="Status" options={fixtureStatusOptions} />
                    <Select name="certainty" label="Certainty" options={certaintyCategoryOptions} />
                    <Select
                      name="privacyLevel"
                      label="Privacy Level"
                      options={privacyLevelOptions}
                    />
                  </div>
                </div>

                {/* Commercial Terms */}
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                    Commercial Terms
                  </h3>
                  <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                    <Input
                      name="primaryComm"
                      label="Primary Commission (%)"
                      type="number"
                      placeholder="0.00"
                      step="0.01"
                    />
                    <Input
                      name="addressComm"
                      label="Address Commission (%)"
                      type="number"
                      placeholder="0.00"
                      step="0.01"
                    />
                  </div>
                </div>

                {/* Additional Information (Optional) */}
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                    Additional Information
                    <span className="ml-2 text-sm font-normal text-gray-500 dark:text-gray-400">
                      (Optional)
                    </span>
                  </h3>
                  <div className="space-y-4">
                    <Autocomplete
                      name="primaryBroker"
                      label="Primary Broker"
                      placeholder="Search for broker"
                      options={brokerOptions}
                      loading={loading}
                    />

                    <Autocomplete
                      name="rateAssessor"
                      label="Rate Assessor"
                      placeholder="Search for assessor"
                      options={rateAssessorOptions}
                      loading={loading}
                    />
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex items-center justify-end space-x-4 border-t border-gray-200 pt-6 dark:border-gray-700">
                  <Button type="button" variant="outline" onClick={onClose} size="lg">
                    Cancel
                  </Button>
                  <Button type="submit" disabled={isSubmitting} size="lg" className="min-w-36">
                    {isSubmitting ? 'Updating...' : 'Update Cargo'}
                  </Button>
                </div>
              </Form>
            );
          }}
        </Formik>
      </div>
    </Modal>
  );
}
